import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

// Shared state for the analyzer, history, shared-with-me and admin pages.
// (Moved out of SSATAnalyzer.js so each page can live on its own route; the API calls are unchanged.)
const AnalysisContext = createContext(null);
const PREVIEW = process.env.REACT_APP_PREVIEW === '1';   // review-only builds (see src/preview)
export const useAnalysis = () => {
  const ctx = useContext(AnalysisContext);
  if (!ctx) throw new Error('useAnalysis must be used within an AnalysisProvider');
  return ctx;
};

// A saved record from the API ({ analysis: { result|results, recommendations, dataPoints }, serverCount, ... }) -> results shape the analyzer renders.
export function recordToResults(data) {
  if (!data || !data.analysis) return null;
  if (data.analysis.results) {
    return { success: true, batchMode: true, totalFiles: data.serverCount, successfulFiles: data.serverCount, results: data.analysis.results };
  }
  if (data.analysis.result) {
    return { success: true, analysis: data.analysis.result, recommendations: data.analysis.recommendations, dataPoints: data.analysis.dataPoints };
  }
  return { success: true, ...data.analysis };
}

export function AnalysisProvider({ children }) {
  const { user, getAuthHeaders } = useAuth();
  const userEmail = user?.signInDetails?.loginId || user?.email || user?.username || '';
  const emailRef = useRef('');
  useEffect(() => { if (userEmail) emailRef.current = userEmail; }, [userEmail]);

  const [results, setResults] = useState(null);
  const [activeUploadId, setActiveUploadId] = useState(null);
  const [activeTitle, setActiveTitle] = useState('');
  const [uploads, setUploads] = useState([]);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminData, setAdminData] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [pendingNew, setPendingNew] = useState(false);   // a fresh analysis was just produced; its saved copy shows up in /api/uploads a moment later
  const topIdRef = useRef(null);
  const triesRef = useRef(0);

  const refresh = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers.Authorization) { setLoadingLists(false); return; }
    const email = emailRef.current || userEmail;
    try {
      const res = await fetch('/api/uploads', { headers });
      if (res.ok) setUploads(await res.json());
    } catch (e) { /* ignore */ }
    try {
      const res = await fetch(`/api/shared?email=${encodeURIComponent(email)}`, { headers });
      if (res.ok) setSharedWithMe(await res.json());
    } catch (e) { /* ignore */ }
    try {
      if (email) {
        const res = await fetch(`/api/admin/check?email=${encodeURIComponent(email)}`, { headers });
        if (res.ok) {
          const { isAdmin: admin } = await res.json();
          setIsAdmin(!!admin);
          if (admin) {
            const all = await fetch(`/api/admin/analyses?email=${encodeURIComponent(email)}`, { headers });
            if (all.ok) setAdminData(await all.json());
          }
        }
      }
    } catch (e) { /* ignore */ }
    setLoadingLists(false);
  }, [getAuthHeaders, userEmail]);

  // Load lists on sign-in and again whenever a new analysis is produced.
  useEffect(() => { if (user) refresh(); }, [user, results]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUpload = async (uploadId, title) => {
    try {
      const res = await fetch(`/api/analysis?uploadId=${uploadId}`, { headers: getAuthHeaders() });
      if (!res.ok) return false;
      const data = await res.json();
      const r = recordToResults(data);
      if (!r) return false;
      setActiveUploadId(uploadId);
      setActiveTitle(title || data.customerName || 'Saved analysis');
      setResults(r);
      return true;
    } catch (e) { console.error('Load failed:', e); return false; }
  };

  // Review-only: /?open=N opens the N-th saved analysis on load (used for screenshots). Removed from production builds.
  useEffect(() => {
    if (!PREVIEW) return;
    const n = new URLSearchParams(window.location.search).get('open');
    if (n !== null && uploads[parseInt(n, 10)]) loadUpload(uploads[parseInt(n, 10)].uploadId, uploads[parseInt(n, 10)].customerName);
  }, [uploads.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadShared = (share) => {
    const r = recordToResults(share?.analysis);
    if (!r) return false;
    setActiveUploadId(null);
    setActiveTitle(`${share.analysis?.customerName || 'Shared analysis'} — from ${share.sharerEmail || 'unknown'}`);
    setResults(r);
    return true;
  };

  const loadAdminUpload = async (a) => {
    if (!a || a.deleted) return false;
    try {
      const email = emailRef.current || userEmail;
      const res = await fetch(`/api/admin/analyses/${a.userId}?uploadId=${a.uploadId}&email=${encodeURIComponent(email)}`, { headers: getAuthHeaders() });
      if (!res.ok) return false;
      const data = await res.json();
      const r = recordToResults(data);
      if (!r) return false;
      setActiveUploadId(null);
      setActiveTitle(`${a.customerName || 'Analysis'} — ${a.userEmail && a.userEmail !== 'unknown' ? a.userEmail : 'customer upload'}`);
      setResults(r);
      return true;
    } catch (e) { console.error('Admin load failed:', e); return false; }
  };

  const deleteUpload = async (uploadId) => {
    try {
      await fetch(`/api/analysis/${uploadId}`, { method: 'DELETE', headers: getAuthHeaders() });
      setUploads(prev => prev.filter(u => u.uploadId !== uploadId));
      if (activeUploadId === uploadId) { setActiveUploadId(null); }
      return true;
    } catch (e) { console.error('Delete failed:', e); return false; }
  };

  const shareAnalysis = async (uploadId, recipientEmail) => {
    if (!recipientEmail || !recipientEmail.trim() || !uploadId) return { ok: false, message: 'Enter an email address' };
    try {
      const res = await fetch('/api/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ recipientEmail: recipientEmail.trim(), uploadId, sharerEmail: emailRef.current || userEmail }),
      });
      return res.ok ? { ok: true, message: 'Shared!' } : { ok: false, message: 'Failed to share' };
    } catch (e) { return { ok: false, message: 'Failed to share' }; }
  };

  // Called by the analyzer right after it receives a new result, so Share can target the freshly saved upload.
  const markNewAnalysis = () => { topIdRef.current = uploads[0]?.uploadId || null; triesRef.current = 0; setActiveUploadId(null); setActiveTitle(''); setPendingNew(true); };
  useEffect(() => {
    if (!pendingNew) return undefined;
    if (uploads[0] && uploads[0].uploadId !== topIdRef.current) { setActiveUploadId(uploads[0].uploadId); setPendingNew(false); return undefined; }
    if (triesRef.current >= 6) { setPendingNew(false); return undefined; }
    const timer = setTimeout(() => { triesRef.current += 1; refresh(); }, 1500);
    return () => clearTimeout(timer);
  }, [pendingNew, uploads]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearResults = () => { setResults(null); setActiveUploadId(null); setActiveTitle(''); };

  const value = {
    userEmail, results, setResults, activeUploadId, setActiveUploadId, activeTitle, setActiveTitle, clearResults,
    uploads, sharedWithMe, isAdmin, adminData, loadingLists, refresh, markNewAnalysis,
    loadUpload, loadShared, loadAdminUpload, deleteUpload, shareAnalysis,
  };
  return <AnalysisContext.Provider value={value}>{children}</AnalysisContext.Provider>;
}
